import React from 'react';
import { createPortal } from 'react-dom';
import { motion, useDragControls, useMotionValue } from 'motion/react';
import { ListChecks, X, GripHorizontal } from 'lucide-react';
import { useTodoBoard, TodoBoardContent, TODO_WIDGET_ENABLED_KEY, TODO_WIDGET_TOGGLE_EVENT } from './TodoModule';

const POSITION_KEY = 'iamani-todo-widget-position';
const PANEL_WIDTH = 420;
const BUBBLE_SIZE = 56;

type Position = { top: number; left: number };

const defaultPosition = (): Position => ({ top: window.innerHeight - 80, left: 24 });

const readPosition = (): Position => {
    try {
        const raw = localStorage.getItem(POSITION_KEY);
        if (raw) return JSON.parse(raw);
    } catch { /* ignore */ }
    return defaultPosition();
};

const clamp = (value: number, max: number) => Math.min(Math.max(value, 8), max);

/** Distance de glisser en dessous de laquelle un geste compte comme un clic, pas un déplacement. */
const CLICK_VS_DRAG_THRESHOLD = 4;

/**
 * Widget ToDo flottant, déplaçable à la souris — activé/désactivé depuis l'écran ToDo
 * (bouton "Widget flottant"), monté une seule fois globalement dans App.tsx pour rester
 * visible quel que soit le module consulté. État (activé/position) mémorisé localement
 * (localStorage) uniquement — pas de synchronisation entre appareils (demandé par Emeni
 * le 2026-08-16 : simple, pas de nouvelle colonne serveur pour ce premier lot).
 *
 * ⚠️ Rendu via un portail directement sur `document.body`, pas à l'endroit où le composant
 * est monté dans l'arbre React : un `position: fixed` se positionne par rapport à un ancêtre
 * animé (transform actif) au lieu du vrai écran si l'un des parents en a un, ce qui faisait
 * disparaître le widget une fois déplacé loin de son point de départ — retour d'Emeni le
 * 2026-08-16. Le portail élimine complètement ce risque, quel que soit l'endroit du montage.
 *
 * ⚠️ La position est entièrement possédée par les valeurs `x`/`y` de framer-motion (celles-là
 * même qui pilotent le glisser), jamais par `top`/`left` en parallèle : les deux mécanismes
 * de positionnement se cumulaient sinon (le glisser applique sa propre transformation CSS,
 * indépendante du style React), et le widget ne restait pas où on le lâchait.
 *
 * ⚠️ Le panneau ouvert s'ancre au coin de la bulle qui laisse le plus de place à l'écran
 * (haut/bas, gauche/droite), recalculé à CHAQUE ouverture — sinon, une bulle posée près du
 * bas de l'écran ouvrait un panneau qui débordait sous la fenêtre, invisible.
 */
export const FloatingTodoWidget: React.FC = () => {
    const [enabled, setEnabled] = React.useState(() => localStorage.getItem(TODO_WIDGET_ENABLED_KEY) === 'true');
    const [open, setOpen] = React.useState(false);
    const [openUp, setOpenUp] = React.useState(false);
    const [openLeft, setOpenLeft] = React.useState(false);
    const dragControls = useDragControls();
    const board = useTodoBoard();
    const x = useMotionValue(0);
    const y = useMotionValue(0);
    // `onClick` et le début du glisser (`onPointerDown` → dragControls.start) se déclenchent sur le
    // MÊME geste (la bulle sert à la fois de poignée et de bouton) : sans ce suivi, le moindre pixel
    // de mouvement pendant le clic ouvrait le widget au lieu de le déplacer.
    const dragDistance = React.useRef(0);

    React.useEffect(() => {
        const pos = readPosition();
        x.set(pos.left);
        y.set(pos.top);
    }, [x, y]);

    React.useEffect(() => {
        const handler = (e: any) => setEnabled(!!e.detail);
        window.addEventListener(TODO_WIDGET_TOGGLE_EVENT, handler);
        return () => window.removeEventListener(TODO_WIDGET_TOGGLE_EVENT, handler);
    }, []);

    const handleDrag = (_e: any, info: { offset: { x: number; y: number } }) => {
        dragDistance.current = Math.hypot(info.offset.x, info.offset.y);
    };

    const handleDragEnd = () => {
        const next: Position = {
            left: clamp(x.get(), window.innerWidth - BUBBLE_SIZE),
            top: clamp(y.get(), window.innerHeight - BUBBLE_SIZE),
        };
        x.set(next.left);
        y.set(next.top);
        localStorage.setItem(POSITION_KEY, JSON.stringify(next));
    };

    const handleLauncherClick = () => {
        if (dragDistance.current < CLICK_VS_DRAG_THRESHOLD) {
            // Estimation raisonnable de la hauteur du panneau (jusqu'à 70vh, plafonnée à 520px) —
            // le contenu réel n'est pas encore monté au moment de décider du sens d'ouverture.
            const estimatedHeight = Math.min(window.innerHeight * 0.7, 520);
            setOpenUp(y.get() + estimatedHeight > window.innerHeight - 16);
            setOpenLeft(x.get() + PANEL_WIDTH > window.innerWidth - 16);
            setOpen(true);
            // Le widget garde sa propre instance de useTodoBoard, chargée une seule fois au montage
            // global de l'app — un tableau créé/modifié depuis l'écran ToDo pendant ce temps n'y
            // apparaissait jamais (remonté par Emeni : le widget restait bloqué sur "Aucun tableau").
            // Rafraîchi à chaque ouverture plutôt qu'en continu.
            board.refreshAll();
        }
        dragDistance.current = 0;
    };

    if (!enabled) return null;

    const widget = (
        <motion.div
            drag
            dragControls={dragControls}
            dragListener={false}
            dragMomentum={false}
            onDrag={handleDrag}
            onDragEnd={handleDragEnd}
            style={{ position: 'fixed', top: 0, left: 0, x, y, zIndex: 9999 }}
            className="select-none"
        >
            <div style={{ position: 'relative', width: BUBBLE_SIZE, height: BUBBLE_SIZE }}>
                {/* ⚠️ Ni AnimatePresence ni initial/animate/exit ici volontairement : l'animation
                    d'ouverture restait bloquée à opacity:0 (panneau présent dans le DOM,
                    fonctionnel, mais invisible) — probablement un conflit avec le geste de
                    glisser actif sur le parent (dragControls.start appelé à chaque clic). Un
                    affichage instantané, sans animation, est fiable dans tous les cas — le
                    mouvement n'est qu'un mouvement, rien n'en dépend (retour d'Emeni 2026-08-16 :
                    panneau resté invisible/bloqué après ouverture). */}
                {open ? (
                    <div
                        style={{
                            position: 'absolute',
                            width: PANEL_WIDTH,
                            maxHeight: '70vh',
                            ...(openUp ? { bottom: 0 } : { top: 0 }),
                            ...(openLeft ? { right: 0 } : { left: 0 }),
                        }}
                        className="bg-surface border border-border rounded-2xl shadow-2xl flex flex-col overflow-hidden"
                    >
                        <div
                            onPointerDown={(e) => dragControls.start(e)}
                            className="flex items-center justify-between px-4 py-2.5 border-b border-border cursor-grab active:cursor-grabbing bg-surface-soft shrink-0"
                        >
                            <span className="flex items-center gap-2 text-sm font-bold text-content">
                                <GripHorizontal size={14} className="text-content-muted" /> ToDo
                            </span>
                            <button onClick={() => setOpen(false)} className="text-content-muted hover:text-content"><X size={16} /></button>
                        </div>
                        <div className="p-3 overflow-y-auto">
                            <TodoBoardContent board={board} compact />
                        </div>
                    </div>
                ) : (
                    <button
                        onPointerDown={(e) => dragControls.start(e)}
                        onClick={handleLauncherClick}
                        style={{ position: 'absolute', top: 0, left: 0, width: BUBBLE_SIZE, height: BUBBLE_SIZE }}
                        className="rounded-full bg-[var(--brand-color)] text-white shadow-2xl flex items-center justify-center cursor-grab active:cursor-grabbing"
                        title="Ouvrir le ToDo"
                    >
                        <ListChecks size={22} />
                    </button>
                )}
            </div>
        </motion.div>
    );

    return createPortal(widget, document.body);
};

export default FloatingTodoWidget;
